/**
 * useProjectDataFull — Fetches project + activities + exchanges in a single API call.
 *
 * Uses the authenticated /workspace/project/{projectId} endpoint that bundles
 * the N+1 server-side (same pattern as the spectator endpoint).
 */
import { useState, useCallback, useRef } from 'react'
import { apiClient } from '@/lib/api/client'
import type { ProjectDetail, ActivityWithExchanges } from '@/lib/types/project-graph'

interface UseProjectDataFullReturn {
  project: ProjectDetail | null
  activities: ActivityWithExchanges[]
  loading: boolean
  error: string | null
  loadProject: (projectId: string) => Promise<void>
  refreshProject: () => Promise<void>
  clearProject: () => void
}

export function useProjectDataFull(): UseProjectDataFullReturn {
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [activities, setActivities] = useState<ActivityWithExchanges[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentProjectIdRef = useRef<string | null>(null)
  const fetchIdRef = useRef(0)

  const fetchProjectData = useCallback(async (projectId: string, clearFirst: boolean) => {
    const myFetchId = ++fetchIdRef.current

    if (clearFirst) {
      setActivities([])
      setProject(null)
    }
    setLoading(true)
    setError(null)
    currentProjectIdRef.current = projectId

    try {
      const resp = await apiClient.get(`/workspace/project/${projectId}`, {
        params: { _t: Date.now() },
      })

      if (fetchIdRef.current !== myFetchId) return

      const data = resp.data
      setProject(data.project || null)
      setActivities(data.activities || [])
    } catch (err) {
      if (fetchIdRef.current !== myFetchId) return
      const message = err instanceof Error ? err.message : 'Failed to load project'
      setError(message)
    } finally {
      if (fetchIdRef.current === myFetchId) {
        setLoading(false)
      }
    }
  }, [])

  const loadProject = useCallback(
    async (projectId: string) => {
      await fetchProjectData(projectId, true)
    },
    [fetchProjectData],
  )

  const refreshProject = useCallback(async () => {
    if (currentProjectIdRef.current) {
      await fetchProjectData(currentProjectIdRef.current, false)
    }
  }, [fetchProjectData])

  const clearProject = useCallback(() => {
    setProject(null)
    setActivities([])
    setError(null)
    currentProjectIdRef.current = null
  }, [])

  return {
    project,
    activities,
    loading,
    error,
    loadProject,
    refreshProject,
    clearProject,
  }
}
